import useSWR from 'swr';
import { getFraudSignals } from 'api/generated/api/fraud-signals';
import { fraudFindingsVisibilityKey } from 'hooks/orgKeys';
import { useSupportUser } from 'hooks/useSupportUser';
import { useOrgId } from 'hooks/useUserSession';

/**
 * Whether fraud risk findings may be shown to the current user.
 *
 * With a campaignId: whether that job's effective fraud risk setting is on (the job's own value,
 * or the org default when the job does not override it). Without one: whether any job in the org
 * could show findings, for org-wide surfaces such as the Interviews page. The server applies the
 * same rule to the data itself, so a hidden request comes back without grades either way.
 *
 * A support user signed in to an org (impersonating, with or without Full Access) sees its
 * findings everywhere, matching the server's is_acting_as_another_user check, so no request is
 * made. While loading, and if the request fails, this returns false: it is a disclosure gate, and
 * a failed answer hides the fraud UI rather than flashing it. `enabled` false (a job that is not
 * saved yet, so the server has nothing to answer for) skips the request and returns false.
 */
export function useFraudSignalsVisible(
  campaignId?: string,
  enabled = true
): boolean {
  const { supportModeActive } = useSupportUser();
  const orgId = useOrgId();
  const { data } = useSWR(
    supportModeActive || !enabled
      ? null
      : fraudFindingsVisibilityKey(orgId, campaignId),
    ([, , currentCampaignId]) =>
      getFraudSignals()
        .fraudSignalsGetFraudFindingsVisibility(
          currentCampaignId ? { campaign_id: currentCampaignId } : undefined
        )
        .then((response) => response.visible),
    {
      // A 403/404 (another org's job, a deleted job) never recovers on retry.
      shouldRetryOnError: false,
      // The transcript drawer starts this read beside core call details and its
      // tab hosts mount once those settle, so this window has to outlast them.
      dedupingInterval: 10 * 1_000,
    }
  );
  return supportModeActive || data === true;
}
